/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { NativeRadio, Text } from '@/shared/ui';
import { formatOlxDealPrice, type OlxDeal, type OlxDealType } from './olx-deal';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The line telling where the deal that was not chosen goes. */
function otherDealNote(t: Translate, deals: OlxDeal[], choice: OlxDealType): string {
  const other = deals.find((deal) => deal.type !== choice);
  if (!other) return '';
  const key = choice === 'sale' ? 'publish.olxDeal.noteSale' : 'publish.olxDeal.noteRental';
  return t(key, { price: formatOlxDealPrice(other.price) });
}

/**
 * "Anúncio principal na OLX": shown while OLX is selected for a property
 * offered for sale AND rent. OLX takes one price per ad, so the broker picks
 * the main deal -- it fixes the ad's type and price -- and the other price is
 * written in the description. Publishing waits for the choice.
 */
export function OlxDealPanel({ t, deals, choice, setChoice }: {
  t: Translate;
  deals: OlxDeal[];
  choice: OlxDealType | null;
  setChoice: (value: OlxDealType) => void;
}) {
  return (
    <div className="space-y-(--space-1-5)" role="radiogroup" aria-label={t('publish.olxDeal.title')} data-testid="olx-deal-panel">
      <Text as="span" className="text-xs font-semibold text-[var(--text-primary)]">{t('publish.olxDeal.title')}</Text>
      <Text className="m-0 text-xs leading-relaxed text-[var(--text-secondary)]">{t('publish.olxDeal.explain')}</Text>
      {deals.map((deal) => (
        <Text as="label" key={deal.type} className="flex items-center gap-(--space-2) cursor-pointer">
          <NativeRadio
            name="olx-deal"
            checked={choice === deal.type}
            onChange={() => setChoice(deal.type)}
            className="w-3.5 h-3.5 accent-[var(--seed-base)]"
            data-testid={`olx-deal-${deal.type}`}
          />
          <Text as="span" className="text-xs font-medium text-[var(--text-primary)]">
            {t(`publish.olxDeal.${deal.type}`, { price: formatOlxDealPrice(deal.price) })}
          </Text>
        </Text>
      ))}
      <Text className="m-0 text-xs text-[var(--text-tertiary)]" data-testid="olx-deal-hint">
        {choice ? otherDealNote(t, deals, choice) : t('publish.olxDeal.required')}
      </Text>
    </div>
  );
}
